import { ChevronDown, ChevronUp, Maximize2 } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { cn } from '@/lib/utils';
import type { SingleMetricStats } from '@/types/metrics';
import { PeriodStatsRow } from '@/components/common/PeriodStatsRow';

interface StatusCardProps {
  title: string;
  value: number;
  variant?: 'default' | 'success' | 'danger';
  icon?: React.ReactNode;
  tooltip?: string;
  onClick?: () => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onShowAll?: () => void;
  children?: React.ReactNode;
  periodStats?: SingleMetricStats;
  periodStatsLoading?: boolean;
  invertDelta?: boolean;
}

export function StatusCard({
  title,
  value,
  variant = 'default',
  icon,
  tooltip,
  onClick,
  expanded,
  onToggleExpand,
  onShowAll,
  children,
  periodStats,
  periodStatsLoading,
  invertDelta,
}: StatusCardProps) {
  const variantClasses = {
    default: 'dls-text-foreground',
    success: 'dls-text-status-online',
    danger: 'dls-text-status-offline',
  };

  const canExpand = onToggleExpand && children && value > 0;

  return (
    <Card
      className={cn(
        onClick &&
          value > 0 &&
          'dls-cursor-pointer hover:dls-ring-2 hover:dls-ring-offset-1 hover:dls-ring-current/20 dls-transition-all'
      )}
      onClick={onClick && value > 0 ? onClick : undefined}
    >
      <CardHeader className="dls-px-4 dls-pb-2">
        <CardTitle className="dls-text-sm dls-font-medium dls-text-muted-foreground dls-flex dls-items-center dls-gap-1.5 dls-whitespace-nowrap">
          {icon}
          {title}
          {tooltip && <InfoTooltip text={tooltip} />}
          {canExpand && (
            <button
              type="button"
              className="dls-ml-auto dls-text-muted-foreground hover:dls-text-foreground dls-transition-colors"
              onClick={(e) => { e.stopPropagation(); onToggleExpand!(); }}
            >
              {expanded ? <ChevronUp className="dls-h-4 dls-w-4" /> : <ChevronDown className="dls-h-4 dls-w-4" />}
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="dls-px-4 dls-pb-3">
        <div
          role={canExpand ? 'button' : undefined}
          tabIndex={canExpand ? 0 : undefined}
          onClick={
            canExpand
              ? (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onToggleExpand!();
                }
              : undefined
          }
          onKeyDown={
            canExpand
              ? (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleExpand!();
                  }
                }
              : undefined
          }
          className={cn('dls-w-full dls-text-left dls-min-h-20', canExpand && 'dls-cursor-pointer')}
        >
          <div>
            <span className={cn('dls-text-4xl dls-font-bold', variantClasses[variant])}>{value}</span>
            {periodStats && (
              <div className="dls-mt-1">
                <PeriodStatsRow
                  stats={periodStats}
                  format={(v) => String(Math.round(v))}
                  invertDelta={invertDelta}
                  loading={periodStatsLoading}
                />
              </div>
            )}
          </div>
        </div>
        {expanded && children && (
          <div className="dls-mt-3 dls-pt-3 dls-border-t">
            <div className="dls-max-h-[300px] dls-overflow-y-auto">{children}</div>
            {onShowAll && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onShowAll();
                }}
                className="dls-mt-2 dls-pt-2 dls-border-t dls-w-full dls-flex dls-items-center dls-justify-center dls-gap-1.5 dls-text-xs dls-text-muted-foreground hover:dls-text-foreground dls-transition-colors"
              >
                <Maximize2 className="dls-h-3 dls-w-3" />
                Show All
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
